import { useCallback, useMemo } from 'react';

export const validate = (value: number, { min, max }: { min: number; max: number }) => {
  if (value < min) return false;
  if (value > max) return false;
  return true;
};

export const getValidValue = (value: number, { min, max }: { min: number; max: number }) => {
  if (value < min) return min;
  if (value > max) return max;
  return value;
};

type useInputStepperProps<T extends string = 'error'> = {
  value: number | null | undefined;
  onChange?: (value: number | null, validValue?: number) => void;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  status?: T;
};

export const useInputStepper = <T extends string = 'error'>({
  value,
  onChange,
  min,
  max,
  step,
  disabled,
  status,
}: useInputStepperProps<T>) => {
  const current_status = (value == null ? status : validate(value, { min, max }) ? status : status ?? 'error') as T;
  const min_disabled = disabled || typeof value !== 'number' || value <= min;
  const max_disabled = disabled || (value != null && value >= max);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.replace(/[^0-9]/g, '');
      if (value === '' || value === null) {
        onChange?.(null);
      } else {
        const val = Number(value) || min;
        onChange?.(val, getValidValue(val, { min, max }));
      }
    },
    [onChange, min, max],
  );

  const handleStep = useCallback(
    (add_value: number) => {
      const to_value = value == null ? (add_value > 0 ? add_value : min) : value + add_value;
      onChange?.(to_value, getValidValue(to_value, { min, max }));
    },
    [onChange, value, min, max],
  );

  const minus_props = useMemo(
    () => ({
      disabled: min_disabled,
      onClick: handleStep.bind(null, -step),
    }),
    [min_disabled, handleStep],
  );

  const plus_props = useMemo(
    () => ({
      disabled: max_disabled,
      onClick: handleStep.bind(null, step),
    }),
    [max_disabled, handleStep],
  );

  return {
    current_status,
    minus_props,
    plus_props,
    handleInputChange,
  };
};
